import { useQuery } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { isQueryReady } from "src/api/axiosClient/utils";
import { Variations } from "src/context/OlapScores/useVariations";
import { NumericalScoreValue, OlapScoresResult } from "../../olapScores/olapScoresSchema";

export const getOrganizationOverviewKey = "getOneCompanyOverview" as const;
type Options = {
    companyId: number | string | undefined;
    campaignId: number | string | undefined;
    variations?: Variations;
};

export const getOrganizationOverview = async (options: Options) => {
    const { companyId, campaignId, variations } = options;

    const url = `/api/organizations/${companyId}/overview/campaign/${campaignId}`;
    const response = await loggedInApiCall.get(url, {
        params: { scores: { variations } },
    });

    const company = response.data as CompanyOverview;
    return company;
};
export const useOrganizationOverview = (options: Options) => {
    const { companyId, campaignId, variations } = options;

    const isEnabled = companyId !== undefined && campaignId !== undefined;
    const query = useQuery({
        queryKey: [getOrganizationOverviewKey, companyId, campaignId, variations],
        cacheTime: 0,
        staleTime: 5 * 60 * 1000,
        enabled: isEnabled,
        queryFn: async () => {
            return getOrganizationOverview({ companyId, campaignId, variations });
        },
    });

    const isReady = isQueryReady(query, isEnabled);

    return {
        overview: query.data,
        isReady,
        ...query,
    };
};

export type CompanyOverview = {
    actionPlans: ActionPlanOverview[];
    scores: ScoreOverview[];
    ssqs: QuestionnaireOverview[];
};

export type ActionPlanOverview = {
    id: string;
    ssqId: number;
    unit: string;

    score: {
        id: number;
        title: string;
    };

    start: { value: NumericalScoreValue; year: string };
    end: { value: NumericalScoreValue; year: string };
};

export type ScoreOverview = {
    id: number;
    isComplete: boolean;
    title: string;
    unit: string;
    value: NumericalScoreValue;
    displayMode: string;
    type: string;
    values: OlapScoresResult;

    isActionPlanAvailable: boolean;
    isTargetAvailable: boolean;

    ssq: {
        id: number;
        title: string;
        displayTitle?: string | null;
    };
};

export type QuestionnaireOverview = {
    id: number;
    title: string;
    displayTitle?: string | null;
    post: {
        id: number;
        ssqVersionId: number;
        completion: number;
        isLocked: boolean;
        canViewSsq: boolean;
        canAnswerSsq: boolean;
        canValidateSsq: boolean;
    } | null;
    scores: QuestionnaireScore[];
};

export type QuestionnaireScore = {
    id: number;

    datavizType: string;
    title: string;
    label: string;

    unit: { abbreviation: string };
};
